import React from 'react';
import { Link } from 'react-router-dom';

const PatientEducation = () => {
  const features = [
    {
      title: "Personalized Content",
      description: "AI-driven educational content tailored to each patient's needs",
      icon: "🎯",
      benefits: [
        "Treatment-specific information",
        "Age-appropriate content",
        "Language preferences",
        "Learning style adaptation"
      ]
    },
    {
      title: "Interactive Learning",
      description: "Engaging multimedia content for better understanding",
      icon: "🎮",
      benefits: [
        "3D treatment visualizations",
        "Interactive diagrams",
        "Video tutorials",
        "Progress tracking"
      ]
    },
    {
      title: "Treatment Planning",
      description: "Comprehensive treatment plan visualization and tracking",
      icon: "📋",
      benefits: [
        "Step-by-step guides",
        "Cost breakdowns",
        "Timeline visualization",
        "Progress monitoring"
      ]
    },
    {
      title: "Knowledge Assessment",
      description: "Track patient understanding and engagement",
      icon: "📊",
      benefits: [
        "Comprehension quizzes",
        "Feedback collection",
        "Engagement metrics",
        "Knowledge gaps identification"
      ]
    }
  ];

  const contentTypes = [
    {
      title: "Treatment Guides",
      description: "Detailed information about specific dental procedures",
      icon: "📚"
    },
    {
      title: "Preventive Care",
      description: "Tips and best practices for maintaining oral health",
      icon: "🦷"
    },
    {
      title: "Post-Treatment Care",
      description: "Instructions for after-care and recovery",
      icon: "🏥"
    },
    {
      title: "FAQ Library",
      description: "Common questions and expert answers",
      icon: "❓"
    }
  ];

  const testimonials = [
    {
      quote: "The personalized educational content has helped our patients better understand their treatment options and make informed decisions.",
      author: "Dr. Sarah Johnson",
      practice: "Modern Dental Care"
    },
    {
      quote: "Our patients love the interactive 3D visualizations. It's made explaining complex procedures much easier.",
      author: "Dr. Robert Chen",
      practice: "Advanced Dental Solutions"
    }
  ];

  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              Patient Education
            </h1>
            <p className="text-xl text-gray-600">
              Personalized educational content and treatment plans that empower patients
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Types */}
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] mb-8 text-center">
            Educational Content
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contentTypes.map((content, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-gray-50 rounded-xl"
              >
                <div className="text-3xl mb-4">
                  {content.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                  {content.title}
                </h3>
                <p className="text-gray-600">
                  {content.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="text-2xl text-orange-500 mb-4">"</div>
              <p className="text-gray-600 mb-6">
                {testimonial.quote}
              </p>
              <div>
                <div className="font-bold text-[#1E2A4A]">
                  {testimonial.author}
                </div>
                <div className="text-gray-600">
                  {testimonial.practice}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Transform Your Patient Education
          </h2>
          <p className="text-xl mb-8">
            Empower your patients with personalized educational content
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/schedule-demo"
              className="bg-white text-orange-500 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule a Demo
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-500 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientEducation; 